import { useBrandContext } from "@/contexts/brandContext";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { ExternalLink } from "lucide-react-native";
import React from "react";
import { Linking } from "react-native";

const prettyHost = (url: string): string => {
  try {
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/**
 * The brand row exactly as a creator sees it in the mobile app: a small logo card
 * with the brand's name and, when there is one, a tappable website link — ported
 * from `apps/mobil/components/montu/campaign/detail-view/BrandRow.tsx` verbatim.
 */
const BrandRow = React.memo(() => {
  const { brand } = useBrandContext();
  const website = brand?.website?.trim() ?? "";
  const tappable = website.length > 0;
  const open = () => {
    if (!tappable) return;
    const href = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    Linking.openURL(href);
  };

  if (!brand) return null;

  const Wrapper = tappable ? Pressable : Box;

  return (
    <Wrapper
      {...(tappable ? { onPress: open } : {})}
      className="rounded-3xl bg-white border border-outline-100 p-3"
    >
      <HStack space="md" className="items-center">
        <Box
          className={`rounded-2xl items-center justify-center overflow-hidden w-11 h-11 border border-outline-50 shrink-0 ${
            brand.image?.url ? "bg-white" : "bg-background-950"
          }`}
        >
          {brand.image?.url ? (
            <Image
              source={{ uri: brand.image.url }}
              alt={brand.name}
              contentFit="contain"
              className="w-[80%] h-[80%]"
            />
          ) : (
            <Text bold size="md" className="text-white">
              {brand.name.charAt(0).toUpperCase()}
            </Text>
          )}
        </Box>
        <VStack className="flex-1" space="xs">
          <Text
            bold
            className="text-typography-950 text-[15px] tracking-tight"
            numberOfLines={1}
          >
            {brand.name}
          </Text>
          {tappable ? (
            <HStack space="xs" className="items-center">
              <Text size="xs" className="text-brand-400" numberOfLines={1}>
                {prettyHost(website)}
              </Text>
              <Icon as={ExternalLink} size="2xs" className="text-brand-400" />
            </HStack>
          ) : null}
        </VStack>
      </HStack>
    </Wrapper>
  );
});

BrandRow.displayName = "BrandRow";

export default BrandRow;
